/**
 * Admin Home Dashboard: universal hub for church management.
 *
 * Structure:
 * - Welcome card with user greeting
 * - Alerts widget for items requiring attention
 * - Quick stats overview (home groups, ministries, members, events)
 * - Sections grid with navigation to all admin areas
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import {
  IconUserCircle,
  IconChevronRight,
} from "../../../components/Icons.tsx";
import { AdminWelcomeCard } from "./AdminWelcomeCard.tsx";
import { AlertsWidget, type AlertItem } from "./AlertsWidget.tsx";
import { QuickStatsGrid } from "./QuickStatsGrid.tsx";
import { AdminSectionsGrid } from "./AdminSectionsGrid.tsx";
import { usePendingCount } from "./hooks";

export function AdminHomePage() {
  const { pendingVerification } = usePendingCount();

  // Build alerts from pending count
  const alerts = useMemo<AlertItem[]>(() => {
    const newAlerts: AlertItem[] = [];
    if (pendingVerification > 0) {
      newAlerts.push({
        id: "pending-payments",
        type: "warning",
        title: "платежей ожидают проверки",
        count: pendingVerification,
        path: "/admin/payments?view=pending",
      });
    }
    return newAlerts;
  }, [pendingVerification]);

  return (
    <Layout>
      {/* Welcome card */}
      <AdminWelcomeCard />

      {/* Alerts requiring attention */}
      <AlertsWidget alerts={alerts} />

      {/* Quick stats overview */}
      <QuickStatsGrid />

      {/* Admin sections */}
      <AdminSectionsGrid />

      {/* Сменить аккаунт — только на главной админки */}
      <div className="mt-6 pt-4 border-t border-slate-200/60">
        <Link
          to="/"
          className="flex items-center gap-3 p-4 rounded-2xl bg-white/95 shadow-sm active:bg-slate-50 transition-colors min-h-[56px] no-underline text-inherit"
        >
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
            <IconUserCircle className="w-6 h-6" />
          </div>
          <span className="flex-1 min-w-0 font-medium text-[15px] text-slate-800">
            Сменить аккаунт
          </span>
          <IconChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
        </Link>
      </div>

      {/* Bottom spacing */}
      <div className="h-4" />
    </Layout>
  );
}
